import type {ReactElement} from 'react';
import {CircularProgressLoader, LOADER_SIZE, LOADER_COLOR} from '@Components/circular-progress-loader';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ExportItemState} from './export-menu-item.types';
import type {ExportMenuItemProps} from './export-menu-item.types';
import styles from './export-menu-item.module.scss';

function getIcon(defaultIcon: string, state: ExportItemState): string {
  if (state === ExportItemState.SUCCESS) {
    return 'icon-check';
  }
  return defaultIcon;
}

function getIconContainerModifier(state: ExportItemState): string {
  if (state === ExportItemState.SUCCESS) {
    return '-success';
  }
  return '-default';
}

function getTitle(titles: ExportMenuItemProps['titles'], state: ExportItemState): string {
  if (state === ExportItemState.PROCESSING) {
    return titles.processing;
  }
  if (state === ExportItemState.SUCCESS) {
    return titles.success;
  }
  return titles.default;
}

function renderIcon(defaultIcon: string, state: ExportItemState): ReactElement {
  if (state === ExportItemState.PROCESSING) {
    return <CircularProgressLoader size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.PRIMARY_DARK} />;
  }
  return <Icon ariaHidden className={styles.icon} icon={getIcon(defaultIcon, state)} size={IconSize.SIZE_ICON_24} />;
}

export function ExportMenuItem({defaultIcon, titles, description, state, onClick}: ExportMenuItemProps): ReactElement {
  const iconContainerModifier = getIconContainerModifier(state);
  const title = getTitle(titles, state);

  return (
    <button type="button" className={styles.itemContainer} aria-label={title} onClick={onClick}>
      <div className={`${styles.iconContainer} ${iconContainerModifier}`}>{renderIcon(defaultIcon, state)}</div>
      <div className={styles.textContainer}>
        <Text val={title} size={TextSize.MD} bold className={styles.title} />
        <Text val={description} size={TextSize.SM} className={styles.description} />
      </div>
    </button>
  );
}
